import * as THREE from 'three';
import { NodeMaterial } from 'three/webgpu';
import {
  uniform, vec3, vec4,
  positionLocal, normalViewGeometry, modelViewMatrix, cameraProjectionMatrix
} from 'three/tsl';

// Inverted-hull outline. Displaced in VIEW space along the view-space normal
// (normalMatrix * normal) and scaled by linear view depth so thickness stays
// constant on screen *and* is independent of per-mesh non-uniform scale
// (bones get squash/stretch via Object3D.scale — doing the offset in local
// space would shrink the outline on squashed bones).
//
// Must use normalViewGeometry, NOT normalView: the latter passes through
// directionToFaceDirection, which flips the normal for BackSide materials —
// which would push this shell inward (invisible, occluded by the main mesh)
// instead of outward. normalViewGeometry is the raw, un-flipped geometry
// normal in view space and matches the original GLSL `normalize(normalMatrix * normal)`.
export class OutlineMaterial extends NodeMaterial {
  private widthUniform = uniform(2.4);
  private colorUniform = uniform(vec3(0, 0, 0));
  private opacityUniform = uniform(1);

  constructor(width = 2.4, color: THREE.ColorRepresentation = '#0a0613', opacity = 1) {
    super();
    this.side = THREE.BackSide;
    this.depthWrite = opacity >= 1;
    this.transparent = opacity < 1;

    const c = new THREE.Color(color);
    this.widthUniform.value = width;
    this.colorUniform.value = new THREE.Vector3(c.r, c.g, c.b);
    this.opacityUniform.value = opacity;

    const viewPos = modelViewMatrix.mul(vec4(positionLocal, 1));
    const scale = viewPos.z.negate().mul(this.widthUniform).mul(0.0018);
    const displaced = viewPos.xyz.add(normalViewGeometry.normalize().mul(scale));
    this.vertexNode = cameraProjectionMatrix.mul(vec4(displaced, 1));
    this.colorNode = this.colorUniform;
    this.opacityNode = this.opacityUniform;
  }

  setColor(c: THREE.ColorRepresentation): void {
    const col = new THREE.Color(c);
    (this.colorUniform.value as THREE.Vector3).set(col.r, col.g, col.b);
  }

  setOpacity(o: number): void {
    this.opacityUniform.value = o;
    this.transparent = o < 1;
    this.depthWrite = o >= 1;
  }
}
